import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OrganizationService } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import SimpleSpinner from "../../loader";
import EmptyState from "../../../newComponents/emptystate";
import { GetImages } from "../../../utils/GetImages";
import { URLS } from "../../../constants/constants";
import "../platform-portal.css";

/**
 * Cross-org list for the Platform portal. `GET /api/Organization` returns ALL organizations for a
 * Platform token (the swagger type is the single-org "my organization" shape, so the payload is
 * cast to an array here). This endpoint is un-paginated, so search + display are done client-side.
 */
const OrganizationsList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await apiHelper(
        OrganizationService.getApiOrganization,
        { showNotification: false }
      );
      const success = res?.data?.success;
      const list = Array.isArray(success) ? success : success ? [success] : [];
      setOrgs(list);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) =>
      `${o?.name || ""} ${o?.email || ""} ${o?.supportEmail || ""}`.toLowerCase().includes(q)
    );
  }, [orgs, search]);

  return (
    <div className="platform-portal-page">
      <div className="top-listing-header-top">
        <h2 className="H2">Organizations</h2>
      </div>

      <div className="platform-search-row">
        <input
          className="platform-search-input"
          placeholder="Search organizations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="normal_text grey_subtext1">{filtered.length} organizations</span>
      </div>

      {loading ? (
        <div className="platform-loadmore">
          <SimpleSpinner />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={GetImages.emptyStateData}
          heading="No organizations found"
          isButton={false}
          isBorder={false}
        />
      ) : (
        <div className="platform-table-wrapper">
          <table className="platform-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Location</th>
                <th>Website</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((org) => (
                <tr
                  key={org?.id}
                  className="clickable platform-organization-row"
                  onClick={() => navigate(`${URLS.PLATFORM_ORGANIZATIONS}/${org?.id}`)}
                >
                  <td className="black fw-500">{org?.name || "—"}</td>
                  <td>{org?.email || org?.supportEmail || "—"}</td>
                  <td>{org?.formatted?.PhoneNumber || org?.phoneNumber || "—"}</td>
                  <td>
                    {org?.address?.city
                      ? `${org.address.city}${org.address.state ? ", " + org.address.state : ""}`
                      : "—"}
                  </td>
                  <td>{org?.website || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrganizationsList;
